import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FormFields } from '@atlaskit/feedback-collector/types';
import FeedbackCollector from '@atlaskit/feedback-collector/FeedbackCollector';
import Link from '@atlaskit/link/link';

import type {
	AiSuggestionsPluginOptions,
	SuggestionFeedbackContext,
} from '@atlassian/editor-plugin-ai-suggestions';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

// Dedicated SEF (Suggested Edits Feedback) feedback-collector entrypoint.
// Project: https://customerfeedback.atlassian.net/jira/servicedesk/projects/SEF
export const SUGGESTED_EDITS_FEEDBACK_ENTRYPOINT_ID = 'cebd2724-7766-4f78-88b7-f61d07050d48';

// Context custom field id (matches `@atlaskit/feedback-collector` conventions).
const FEEDBACK_CONTEXT_FIELD_ID = 'customfield_10047';

// Cloud id of the internal Atlassian "Hello" dogfooding tenant.
const HELLO_CLOUD_ID = 'a436116f-02ce-4520-8fbb-7301462a1674';

const isHelloTenant = (cloudId: string | undefined): boolean => cloudId === HELLO_CLOUD_ID;

const i18n = defineMessages({
	feedbackTitleHelpful: {
		id: 'editor-features.suggested-edits-feedback.title-helpful',
		defaultMessage: 'You found this suggestion helpful',
		description:
			'Title of the feedback dialog shown after a user marks an AI suggested edit as helpful (thumbs up).',
	},
	feedbackTitleUnhelpful: {
		id: 'editor-features.suggested-edits-feedback.title-unhelpful',
		defaultMessage: 'You found this suggestion unhelpful',
		description:
			'Title of the feedback dialog shown after a user marks an AI suggested edit as unhelpful (thumbs down).',
	},
	summaryPlaceholder: {
		id: 'editor-features.suggested-edits-feedback.summary-placeholder',
		defaultMessage: 'Tell us more about this suggestion',
		description:
			'Placeholder text inside the free-text box of the suggested edits feedback dialog.',
	},
	textAreaLabel: {
		id: 'editor-features.suggested-edits-feedback.text-area-label',
		defaultMessage: 'Tell us more about your experience',
		description: 'Label for the free-text box of the suggested edits feedback dialog.',
	},
	canBeContactedLabel: {
		id: 'editor-features.suggested-edits-feedback.can-be-contacted-label',
		defaultMessage:
			'Yes, Atlassian teams can contact me to learn about my experiences to improve Atlassian products and services. I acknowledge the <a>Atlassian Privacy Policy</a>.',
		description:
			'Label next to the checkbox letting the user opt in to being contacted about their feedback. <a> wraps the Atlassian Privacy Policy link.',
	},
	optOutOfResearchLabel: {
		id: 'editor-features.suggested-edits-feedback.opt-out-of-research-label',
		defaultMessage: "I'd like to opt out of product research",
		description:
			'Label next to the checkbox letting the user opt out of Atlassian product research.',
	},
	flagTitle: {
		id: 'editor-features.suggested-edits-feedback.flag-title',
		defaultMessage: 'Thanks!',
		description: 'Title of the success flag shown after suggested edits feedback is submitted.',
	},
	flagDescription: {
		id: 'editor-features.suggested-edits-feedback.flag-description',
		defaultMessage: 'Your feedback helps us improve suggested edits.',
		description:
			'Description of the success flag shown after suggested edits feedback is submitted.',
	},
});

const contextLines = (context: SuggestionFeedbackContext): string[] =>
	[
		`sentiment: ${context.sentiment}`,
		`suggestionId: ${context.suggestionId}`,
		context.suggestionType ? `suggestionType: ${context.suggestionType}` : undefined,
		context.interactionPoint ? `interactionPoint: ${context.interactionPoint}` : undefined,
	].filter((line): line is string => Boolean(line));

// Payload + ADF are too large for a JSD field, so Hello users get them as a
// linked JSON attachment instead.
const shouldAttachPayload = (
	cloudId: string | undefined,
	contentId: string | undefined,
	context: SuggestionFeedbackContext | undefined,
): boolean =>
	isHelloTenant(cloudId) && !!contentId && !!context?.suggestion && !!context?.documentAdf;

// Upload the { response, adf } JSON attachment (loadable into the plugin's basic
// example) and return its download URL, or undefined on failure.
const uploadSuggestionPayloadAttachment = async (
	contentId: string,
	context: SuggestionFeedbackContext,
): Promise<string | undefined> => {
	const json = JSON.stringify(
		{
			response: { ai_feature_output: { items: [context.suggestion] } },
			adf: context.documentAdf,
		},
		null,
		2,
	);
	const fileName = `suggested-edits-feedback-${context.suggestionId}.json`;
	const formData = new FormData();
	formData.append('file', new Blob([json], { type: 'application/json' }), fileName);
	formData.append('comment', 'AI suggested edits feedback payload (Hello only)');

	try {
		const response = await fetch(`/wiki/rest/api/content/${contentId}/child/attachment`, {
			method: 'PUT',
			headers: { 'X-Atlassian-Token': 'no-check' },
			body: formData,
			credentials: 'include',
		});
		if (!response.ok) {
			return undefined;
		}
		const body = (await response.json()) as {
			results?: { _links?: { download?: string; webui?: string; base?: string } }[];
		};
		const result = body.results?.[0];
		const links = result?._links;

		// Prefer the API's `/download/attachments/...` link; some tenants return a
		// non-GETtable REST path, so fall back to the canonical download URL.
		const relativePath =
			links?.download && links.download.includes('/download/attachments/')
				? links.download
				: `/download/attachments/${contentId}/${encodeURIComponent(fileName)}`;

		// Confluence serves attachments under the `/wiki` context path.
		const path = relativePath.startsWith('/wiki') ? relativePath : `/wiki${relativePath}`;
		return `${window.location.origin}${path}`;
	} catch {
		return undefined;
	}
};

// Mirrors `@atlaskit/feedback-collector` defaultProps; used when we take over the
// submission. Keep in sync with `FeedbackCollector`.
const FEEDBACK_FIELDS = {
	summary: 'summary',
	description: 'description',
	customerName: 'customfield_10045',
	canBeContacted: 'customfield_10043',
	enrollInResearch: 'customfield_10044',
	canBeContactedAgree: [{ id: '10109' }],
	canBeContactedDecline: [{ id: '10111' }],
	enrollInResearchAgree: [{ id: '10110' }],
	enrollInResearchDecline: [{ id: '10112' }],
} as const;

const MAX_SUMMARY_LENGTH = 100;

// Submit comment, opt-ins and the payload link as one ticket ourselves (used when
// the collector's own POST is suppressed).
const postSuggestionFeedbackWithLink = ({
	context,
	formValues,
	name,
	atlassianAccountId,
	payloadUrl,
	hasOptedOutOfDataCollection,
}: {
	context: SuggestionFeedbackContext;
	formValues: FormFields;
	name: string | undefined;
	atlassianAccountId: string | undefined;
	payloadUrl: string | undefined;
	hasOptedOutOfDataCollection: boolean;
}): void => {
	const description = formValues.description ?? '';
	const contextValue = [
		...contextLines(context),
		...(atlassianAccountId ? [`atlassian-account-id: ${atlassianAccountId}`] : []),
		`dataCollectionOptOut: ${hasOptedOutOfDataCollection}`,
		...(payloadUrl ? [`payloadUrl: ${payloadUrl}`] : []),
	].join('\n');
	const body = {
		feedback: {
			entrypointId: SUGGESTED_EDITS_FEEDBACK_ENTRYPOINT_ID,
			fields: [
				{ id: FEEDBACK_FIELDS.summary, value: description.slice(0, MAX_SUMMARY_LENGTH) },
				{ id: FEEDBACK_FIELDS.description, value: description },
				{ id: FEEDBACK_FIELDS.customerName, value: name ?? 'unknown' },
				...(formValues.canBeContacted && atlassianAccountId
					? [{ id: 'aaidOrHash', value: atlassianAccountId }]
					: []),
				{
					id: FEEDBACK_FIELDS.canBeContacted,
					value: formValues.canBeContacted
						? FEEDBACK_FIELDS.canBeContactedAgree
						: FEEDBACK_FIELDS.canBeContactedDecline,
				},
				{
					id: FEEDBACK_FIELDS.enrollInResearch,
					value: hasOptedOutOfDataCollection
						? FEEDBACK_FIELDS.enrollInResearchDecline
						: FEEDBACK_FIELDS.enrollInResearchAgree,
				},
				{ id: FEEDBACK_CONTEXT_FIELD_ID, value: contextValue },
			],
		},
	};
	// Base64-encode the UTF-8 JSON body (browser-only, runs on submit).
	const postData = btoa(unescape(encodeURIComponent(JSON.stringify(body))));
	void fetch('/gateway/api/feedback-collector-api/v2/feedback', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ data: postData }),
		credentials: 'include',
	}).catch(() => {
		// Best-effort: don't disrupt the user if the submission fails.
	});
};

const SuggestedEditsFeedbackCollectorComponent = ({
	flags,
	feedbackContext,
	cloudId,
	contentId,
	onClose,
}: {
	flags: FlagsStateContainer;
	feedbackContext?: SuggestionFeedbackContext;
	cloudId?: string;
	contentId?: string;
	onClose: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { userIdForAnalytics, displayName } = useSessionData();

	const canAttachPayload = shouldAttachPayload(cloudId, contentId, feedbackContext);

	const onSubmit = (formValues: FormFields) => {
		const hasOptedOutOfDataCollection = formValues.enrollInResearchGroup === true;

		if (feedbackContext) {
			void (async () => {
				const payloadUrl =
					!hasOptedOutOfDataCollection && canAttachPayload && contentId
						? await uploadSuggestionPayloadAttachment(contentId, feedbackContext)
						: undefined;
				postSuggestionFeedbackWithLink({
					context: feedbackContext,
					formValues,
					name: displayName ?? undefined,
					atlassianAccountId: userIdForAnalytics ?? undefined,
					payloadUrl,
					hasOptedOutOfDataCollection,
				});
			})();
		}

		// Close the dialog once feedback has been sent, then confirm with a flag.
		onClose();
		void flags.showFlag({
			type: 'success-circle',
			title: formatMessage(i18n.flagTitle),
			description: formatMessage(i18n.flagDescription),
		});
	};

	const feedbackTitle =
		feedbackContext?.sentiment === 'good'
			? formatMessage(i18n.feedbackTitleHelpful)
			: formatMessage(i18n.feedbackTitleUnhelpful);

	return (
		<FeedbackCollector
			name={displayName ?? undefined}
			atlassianAccountId={userIdForAnalytics ?? undefined}
			onClose={onClose}
			onSubmit={onSubmit}
			// Blank the entrypoint so the collector skips its own POST and we submit
			// the single, complete ticket ourselves in onSubmit.
			entrypointId=""
			showTypeField={false}
			feedbackTitle={feedbackTitle}
			showRequiredFieldsSummary={false}
			// Contact + product-research checkboxes (contact links to privacy policy).
			canBeContactedLabel={
				<>
					{formatMessage(i18n.canBeContactedLabel, {
						a: (chunks: React.ReactNode[]) => (
							<Link href="https://www.atlassian.com/legal/privacy-policy" target="_blank">
								{chunks}
							</Link>
						),
					})}
				</>
			}
			enrolInResearchLabel={formatMessage(i18n.optOutOfResearchLabel)}
			customTextAreaLabel={formatMessage(i18n.textAreaLabel)}
			summaryPlaceholder={formatMessage(i18n.summaryPlaceholder)}
		/>
	);
};

const SuggestedEditsFeedbackCollectorWithFlags = withFlags(
	SuggestedEditsFeedbackCollectorComponent,
);

export interface UseSuggestedEditsPluginOptionsProps {
	cloudId: string;
	contentId: string | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ConversationAssistant?: React.ComponentType<{ children: React.ReactNode }>;
	onShowSidebar?: () => void;
}

export interface UseSuggestedEditsPluginOptions {
	FeedbackCollector: React.FC;
	pluginOptions: AiSuggestionsPluginOptions;
}

/**
 * Single source of truth for the AI suggested-edits plugin configuration in the
 * Confluence editor (mirrors the shape of `useLoomPluginOptions` /
 * `useTrackChangesPluginOptions`).
 *
 * Owns both the plugin options **and** the feedback flow: when a user marks a
 * suggestion as helpful/unhelpful, the returned `onSubmitSuggestionFeedback`
 * callback opens the Suggested Edits Feedback (SEF) feedback collector dialog so
 * the user can optionally add free-text feedback before it is submitted.
 *
 * @example
 * const { pluginOptions, FeedbackCollector } = useSuggestedEditsPluginOptions({
 *   cloudId,
 *   contentId,
 *   ConversationAssistant,
 *   onShowSidebar,
 * });
 * // pass pluginOptions to aiSuggestionsPlugin, and render <FeedbackCollector />
 */
export const useSuggestedEditsPluginOptions = ({
	cloudId,
	contentId,
	ConversationAssistant,
	onShowSidebar,
}: UseSuggestedEditsPluginOptionsProps): UseSuggestedEditsPluginOptions => {
	const [feedbackContext, setFeedbackContext] = useState<SuggestionFeedbackContext>();

	const onClose = useCallback(() => {
		setFeedbackContext(undefined);
	}, []);

	const openFeedbackCollector = useCallback((context: SuggestionFeedbackContext) => {
		setFeedbackContext(context);
	}, []);

	const pluginOptions = useMemo(
		(): AiSuggestionsPluginOptions => ({
			contentAri: `ari:cloud:confluence:${cloudId}:page/${contentId}`,
			onShowSidebar,
			ConversationAssistant,
			// Open the SEF feedback collector so the user can add additional
			// feedback. The plugin is only enabled when
			// `platform_editor_ai_content_suggested_edits` is on, so this is
			// already behind that gate upstream.
			onSubmitSuggestionFeedback: openFeedbackCollector,
		}),
		[cloudId, contentId, onShowSidebar, ConversationAssistant, openFeedbackCollector],
	);

	const SuggestedEditsFeedbackCollector = useCallback(
		() =>
			feedbackContext ? (
				<SuggestedEditsFeedbackCollectorWithFlags
					feedbackContext={feedbackContext}
					cloudId={cloudId}
					contentId={contentId}
					onClose={onClose}
				/>
			) : null,
		[feedbackContext, cloudId, contentId, onClose],
	);

	return {
		FeedbackCollector: SuggestedEditsFeedbackCollector,
		pluginOptions,
	};
};
